import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { User, Check, X, Copy, ChevronDown, ChevronUp, Search } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

const UsersReferralsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedUser, setExpandedUser] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    const fetchReferralsData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        
        // Usando axios y la URL base correcta con parámetros de paginación
        const response = await axios.get(`${API_URL}/api/v1/referrals/admin/users-with-referrals`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            page: currentPage,
            limit: itemsPerPage
          }
        });
        
        // Asumiendo que el backend devuelve { data: [...], pagination: { totalItems, totalPages } }
        // Si el backend aún no soporta paginación, adaptamos la respuesta actual
        if (response.data.pagination) {
          setUsers(response.data.data);
          setTotalItems(response.data.pagination.totalItems);
          setTotalPages(response.data.pagination.totalPages);
        } else {
          // Adaptación temporal si el backend no soporta paginación
          const allUsers = response.data;
          setTotalItems(allUsers.length);
          setTotalPages(Math.ceil(allUsers.length / itemsPerPage));
          
          // Simulamos paginación en el frontend
          const startIndex = (currentPage - 1) * itemsPerPage;
          const endIndex = startIndex + itemsPerPage;
          setUsers(allUsers.slice(startIndex, endIndex));
        }
      } catch (err) {
        console.error("Error al cargar datos de referidos:", err.response || err);
        setError(
          err.response?.data?.message || 
          `Error al cargar los datos (${err.message}). Por favor, verifica que estés conectado y tengas permisos de administrador.`
        );
      } finally {
        setLoading(false);
      }
    };

    fetchReferralsData();
  }, [currentPage, itemsPerPage]);

  const toggleExpand = (userId) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Funciones para la paginación
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const filteredUsers = searchTerm
    ? users.filter(
        (user) =>
          user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : users;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error:</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Barra de búsqueda */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={18} className="text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Buscar usuario por nombre o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Contador de resultados */}
      <div className="mb-4 text-gray-400">
        Mostrando {filteredUsers.length} usuarios {searchTerm ? "filtrados" : `(página ${currentPage} de ${totalPages})`} | Total: {totalItems} usuarios
      </div>

      {/* Lista de usuarios con sus códigos */}
      {filteredUsers.length > 0 ? (
        <div className="space-y-4">
          {filteredUsers.map((user) => (
            <div
              key={user.id}
              className="border border-gray-700 rounded-lg overflow-hidden bg-gray-800"
            >
              {/* Cabecera del usuario (siempre visible) */}
              <div
                className="p-4 flex flex-wrap md:flex-nowrap items-center justify-between cursor-pointer hover:bg-gray-750"
                onClick={() => toggleExpand(user.id)}
              >
                <div className="flex items-center space-x-4 mb-2 md:mb-0">
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                    <User size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{user.name}</h3>
                    <p className="text-gray-400">{user.email}</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap items-center gap-3 md:gap-5">
                  <div className="text-center">
                    <div className="text-sm text-gray-400">Plan</div>
                    <div className="font-medium text-blue-400">{user.currentPlan || "Ninguno"}</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-sm text-gray-400">Códigos</div>
                    <div className="font-medium text-white">{user.referralCodes?.length || 0}</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-sm text-gray-400">Referidos</div>
                    <div className="font-medium text-white">{user.referredUsers?.length || 0}</div>
                  </div>
                  
                  <div className="ml-2">
                    {expandedUser === user.id ? (
                      <ChevronUp size={20} className="text-blue-400" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Contenido expandible */}
              {expandedUser === user.id && (
                <div className="border-t border-gray-700 p-4">
                  {/* Sección de códigos de referido */}
                  <div className="mb-6">
                    <h4 className="text-lg font-medium text-white mb-3">Códigos de Referido</h4>
                    
                    {user.referralCodes && user.referralCodes.length > 0 ? (
                      <div className="bg-gray-900 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-700">
                            <thead className="bg-gray-800">
                              <tr>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Código</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Estado</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Creado</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Expira</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Usos</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Acciones</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                              {user.referralCodes.map((code) => (
                                <tr key={code.id} className="hover:bg-gray-750">
                                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-white">{code.code}</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                                    {code.active ? (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900 text-green-300">
                                        <Check size={14} className="mr-1" /> Activo
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-900 text-red-300">
                                        <X size={14} className="mr-1" /> Inactivo
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                                    {new Date(code.createdAt).toLocaleDateString()}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                                    {code.expiresAt ? new Date(code.expiresAt).toLocaleDateString() : "Sin expiración"}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                                    {code.usedCount}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyToClipboard(code.code);
                                      }}
                                      className="inline-flex items-center px-3 py-1 border border-gray-600 text-xs rounded-md bg-gray-800 text-blue-400 hover:bg-gray-700"
                                    >
                                      {copiedCode === code.code ? "¡Copiado!" : (
                                        <>
                                          <Copy size={14} className="mr-1" /> Copiar
                                        </>
                                      )}
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400">Este usuario no tiene códigos de referido.</p>
                    )}
                  </div>
                  
                  {/* Sección de usuarios referidos */}
                  <div>
                    <h4 className="text-lg font-medium text-white mb-3">Usuarios Invitados (Referidos)</h4>
                    
                    {user.referredUsers && user.referredUsers.length > 0 ? (
                      <div className="bg-gray-900 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-700">
                            <thead className="bg-gray-800">
                              <tr>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Usuario</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Email</th>
                                {/*<th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Código</th>*/}
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Registrado</th>
                                {/*<th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Estado</th>*/}
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Plan</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                              {user.referredUsers.map((referred) => (
                                <tr key={referred.id} className="hover:bg-gray-750">
                                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-white">{referred.name}</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">{referred.email}</td>
                                  {/*<td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">{referred.referralCode}</td>*/}
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                                    {new Date(referred.registeredAt).toLocaleDateString()}
                                  </td>
                                  {/*<td className="px-4 py-3 whitespace-nowrap text-sm">
                                     No tenemos el campo active, así que asumimos que todos los usuarios están activos 
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900 text-green-300">
                                      <Check size={14} className="mr-1" /> Activo
                                    </span>
                                  </td>*/}
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                                    {referred.currentPlan}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400">Este usuario no ha referido a nadie.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 text-center">
          <p className="text-gray-400">No se encontraron usuarios que coincidan con la búsqueda.</p>
        </div>
      )}
      
      {/* Paginación */}
      {!loading && !error && users.length > 0 && !searchTerm && (
        <div className="px-4 py-3 bg-gray-800 border-t border-gray-700 sm:px-6 flex justify-center mt-4">
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-700 text-sm font-medium 
                  ${currentPage === 1 
                    ? 'bg-gray-800 text-gray-500 cursor-not-allowed' 
                    : 'bg-gray-900 text-gray-300 hover:bg-gray-700'}`}
              >
                <span className="sr-only">Anterior</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              
              {/* Números de página */}
              {Array.from({ length: Math.min(5, totalPages) }).map((_, index) => {
                // Cálculo para mostrar las páginas correctas cuando hay muchas
                let pageNumber;
                
                if (totalPages <= 5) {
                  // Si hay 5 o menos páginas, mostrar todas
                  pageNumber = index + 1;
                } else {
                  // Si hay más de 5 páginas, mostrar de forma inteligente
                  if (currentPage <= 3) {
                    pageNumber = index + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + index;
                  } else {
                    pageNumber = currentPage - 2 + index;
                  }
                }
                
                return (
                  <button
                    key={pageNumber}
                    onClick={() => paginate(pageNumber)}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-700 text-sm font-medium
                      ${currentPage === pageNumber
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-900 text-gray-300 hover:bg-gray-700'}`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
              
              <button
                onClick={goToNextPage}
                disabled={currentPage >= totalPages}
                className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-700 text-sm font-medium 
                  ${currentPage === totalPages 
                    ? 'bg-gray-800 text-gray-500 cursor-not-allowed' 
                    : 'bg-gray-900 text-gray-300 hover:bg-gray-700'}`}
              >
                <span className="sr-only">Siguiente</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersReferralsPanel;
